import { device } from '@/styles/device'
import { align } from '@/styles/display'
import spaceStyle from '@/styles/space'
import {
  V1FormElementStateType,
  type V1FormElementState,
  type V1FormElementUiCheckBoxList,
  V1LayoutDirection,
  V1FormElementSetLayoutDirection
} from 'mnvm-mypage-app-ts-if'
import { memo, useState } from 'react'
import styled from 'styled-components'
import { Block } from '../base/Block'
import { Checkbox } from '../base/Form/Checkbox'
import { CheckboxSize } from '../base/Form/Checkbox/Checkbox'
import InputErrorLabel from '../base/Form/InputErrorLabel'
import { Row } from '../base/Layout'
import { Text3 } from '../base/Text'
import type OptionType from './OptionType'
import textAlign from '@/styles/text_align'

interface FormElementCheckBoxListSimpleOption<T extends OptionType> {
  value: T
  label: string
  width?: string
}

interface FormElementCheckBoxListSimpleProps<T extends OptionType> {
  options: Array<FormElementCheckBoxListSimpleOption<T>>
  initialSelectedValues: T[]
  onChange: (newSelectedValues: T[]) => void
  layoutDirection: string
  formElementSetLayout?: string
}

interface CheckboxListContainerProps {
  layoutDirection: string
  flexDirection: string
  gap: string
  formElementSetLayout?: string
}

const CheckboxListContainer = styled.div<CheckboxListContainerProps>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection};
  gap: ${(props) => props.gap};
  flex-wrap: wrap;
  & > div {
    width: ${(props) =>
      props.formElementSetLayout === V1FormElementSetLayoutDirection.TwoColumns
        ? 'calc(50% - 12px)'
        : 'unset'};
  }
  @media ${device.mobile} {
    gap: 12px;
    & > div {
      width: ${(props) =>
        props.layoutDirection === V1LayoutDirection.Horizontal
          ? 'calc(50% - 12px)'
          : '100%'};
    }
  }
`

function FormElementCheckBoxListSimple<T extends OptionType>({
  options,
  initialSelectedValues,
  onChange,
  layoutDirection,
  formElementSetLayout
}: FormElementCheckBoxListSimpleProps<T>): JSX.Element {
  const [selectedValuesState, setSelectedValuesState] = useState<T[]>(
    initialSelectedValues
  )

  const props: CheckboxListContainerProps = {
    layoutDirection,
    flexDirection:
      layoutDirection === V1LayoutDirection.Horizontal ||
      formElementSetLayout === V1FormElementSetLayoutDirection.TwoColumns
        ? 'row'
        : 'column',
    gap: layoutDirection === V1LayoutDirection.Horizontal ? '28px' : '12px',
    formElementSetLayout
  }

  return (
    <CheckboxListContainer {...props}>
      {options.map((option) => {
        const checked = selectedValuesState.includes(option.value)
        return (
          <Row key={option.label}>
            <Checkbox
              checked={checked}
              onChange={() => {
                let newSelectedValueState: T[]

                if (checked) {
                  newSelectedValueState = selectedValuesState.filter(
                    (e) => e !== option.value
                  )
                } else {
                  newSelectedValueState = [...selectedValuesState, option.value]
                }

                setSelectedValuesState(newSelectedValueState)
                onChange(newSelectedValueState)
              }}
              size={CheckboxSize.Size14}
            >
              <Text3
                margin={{ all: spaceStyle.NONE }}
                textAlign={textAlign.START}
              >
                {option.label}
              </Text3>
            </Checkbox>
          </Row>
        )
      })}
    </CheckboxListContainer>
  )
}

export interface FormElementCheckboxListSimpleProps {
  isEdit: boolean
  checkBoxListSimpleUiIFM: V1FormElementUiCheckBoxList
  formElementState: V1FormElementState
  elemenErrorMessage?: string
  onChange: (newElementState: V1FormElementState) => void
  layoutDirection: string
  formElementSetLayout?: string
}

const FormElementCheckboxListSimple = ({
  isEdit,
  checkBoxListSimpleUiIFM,
  formElementState,
  onChange,
  elemenErrorMessage,
  layoutDirection,
  formElementSetLayout
}: FormElementCheckboxListSimpleProps): JSX.Element => {
  if (
    formElementState.type === V1FormElementStateType.Uint32List ||
    formElementState.type === V1FormElementStateType.Uint32 ||
    formElementState.type === V1FormElementStateType.TextList ||
    formElementState.type === V1FormElementStateType.Text
  ) {
    const options: Array<FormElementCheckBoxListSimpleOption<number | string>> =
      checkBoxListSimpleUiIFM.contents?.map(
        (e): FormElementCheckBoxListSimpleOption<number | string> => {
          return {
            label: e.label ?? '',
            value:
              e.state?.uint32 != null ? e.state?.uint32 : e.state?.text ?? ''
          }
        }
      ) ?? []

    const selectedValues: Array<string | number> =
      formElementState.contentStates?.map((e) => {
        return e.uint32 != null ? e.uint32 : e.text ?? ''
      }) ?? []

    if (!isEdit) {
      const selectedLabels = selectedValues
        .map((value) => {
          const matchingOption = options.find(
            (option) => option.value === value
          )
          return matchingOption?.label
        })
        .filter((label) => label !== undefined)

      return (
        <Text3 textAlign={align.LEFT} margin={{ all: spaceStyle.NONE }}>
          {selectedLabels.join('、')}
        </Text3>
      )
    }

    return (
      <Block>
        <FormElementCheckBoxListSimple
          options={options}
          initialSelectedValues={selectedValues}
          onChange={(selectedValues) => {
            onChange({
              ...formElementState,
              contentStates: selectedValues.map((selectedValue) => {
                return typeof selectedValue === 'number'
                  ? {
                      uint32: selectedValue
                    }
                  : {
                      text: selectedValue
                    }
              })
            })
          }}
          layoutDirection={layoutDirection}
          formElementSetLayout={formElementSetLayout}
        />
        {elemenErrorMessage !== undefined && (
          <InputErrorLabel message={elemenErrorMessage} />
        )}
      </Block>
    )
  }

  return (
    <>
      <Block>
        <Checkbox>
          <Text3 margin={{ left: spaceStyle.M }}>指定なし</Text3>
        </Checkbox>
      </Block>
    </>
  )
}

export default memo(FormElementCheckboxListSimple)
